import { type EmployeeLevel, Gender, type Prisma } from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { type AppContext } from "~/lib/context";
import { compact } from "~/lib/lodash";
import { roundTo } from "~/lib/math";
import { arrayHasValues } from "~/lib/utils";
import { whereCompanyIsPartOfDataset } from "~/services/company";
import { CURRENT_LIVE_BENCHMARK } from "~/services/dataset-employee/utils";
import { getAllowedLevels } from "~/services/employee/employeeLevel";
import { getAllowedCountryIds } from "~/services/employee/employeeLocation";
import { getHeadcountRange } from "~/services/headcount";
import { getAllowedJobFamilyIds } from "~/services/job";
import { type GetMarketDataStatsDatasetEmployeesOptions } from "~/services/market-data/getMarketDataStats";

export const getMarketDataDatasetEmployees = async (
  ctx: AppContext,
  options: GetMarketDataStatsDatasetEmployeesOptions = {}
) => {
  const allowedEmployees = await fetchMarketDataDatasetEmployees(ctx, options);

  if (options.useTestData) {
    return allowedEmployees.map(anonymiseMarketDataDatasetEmployee);
  }

  return allowedEmployees;
};

export type MarketDataDatasetEmployee = AsyncReturnType<typeof fetchMarketDataDatasetEmployees>[number];

const fetchMarketDataDatasetEmployees = async (ctx: AppContext, options: GetMarketDataStatsDatasetEmployeesOptions) => {
  return ctx.prisma.datasetEmployee.findMany({
    where: buildMarketDataStatsDatasetEmployeesWhere(ctx, options),
    select: marketDataDatasetEmployeeSelect,
    ...(ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS && {
      distinct: ["companyId", "externalId", "jobId", "locationId", "benchmarkLevelMax", "benchmarkLevelMin"],
    }),
    ...(!ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS && {
      distinct: ["companyId", "externalId", "jobId", "locationId", "level"],
    }),
    orderBy: { liveAt: "desc" },
  });
};

const marketDataDatasetEmployeeSelect = {
  id: true,
  externalId: true,
  companyId: true,
  churnedCompanyId: true,
  locationId: true,
  jobId: true,
  gender: true,
  company: { select: { tags: { select: { id: true } } } },
  level: true,
  benchmarkLevelMin: true,
  benchmarkLevelMax: true,
  baseSalary: true,
  fixedBonus: true,
  onTargetBonus: true,
  job: {
    select: { name: true, familyId: true },
  },
  location: { select: { name: true, country: { select: { name: true, alpha2: true } } } },
  currencyCode: true,
  euroExchangeRate: true,
  lastValidAt: true,
} satisfies Prisma.DatasetEmployeeSelect;

const buildMarketDataStatsDatasetEmployeesWhere = (
  ctx: AppContext,
  options: GetMarketDataStatsDatasetEmployeesOptions
) => {
  const allowedJobFamilyIds = getAllowedJobFamilyIds(ctx.user);
  const allowedCountryIds = getAllowedCountryIds(ctx.user);
  const allowedLevels = getAllowedLevels(ctx.user, options.mergeAdvancedLevels).filter(
    (level) => level !== "MA0X"
  ) as EmployeeLevel[];

  const companySize = ctx.featureFlags.CAN_ACCESS_FIGURES_AI_V2
    ? getHeadcountRange(options.headcount)
    : options.companySize;

  const companyTagsIds = [...(options.labels?.tagIds ?? []), ...(options.industries?.tagIds ?? [])];

  const eighteenMonthsAgo = new Date();
  eighteenMonthsAgo.setMonth(eighteenMonthsAgo.getMonth() - 18);

  return {
    OR: [
      {
        company: whereCompanyIsPartOfDataset(ctx.user),
      },
      {
        companyId: null,
        churnedCompanyId: { not: null },
      },
    ],
    excludedFromDataset: { not: true },
    liveAt: { lte: CURRENT_LIVE_BENCHMARK },

    AND: compact([
      { OR: [{ lastValidAt: null }, { lastValidAt: { gte: eighteenMonthsAgo } }] }, // exclude stale dataset employees
      arrayHasValues(options.companyIds) && { companyId: { in: options.companyIds } },
      options.omitCompanyId && { companyId: { not: options.omitCompanyId } },
      options.fundingRounds && { companyFundingRound: { in: options.fundingRounds } },
      companySize && companySize.min !== null && { companyHeadcount: { gte: companySize.min } },
      companySize && companySize.max !== null && { companyHeadcount: { lte: companySize.max } },
      arrayHasValues(companyTagsIds) && { companyTagsIds: { hasSome: companyTagsIds } },
      arrayHasValues(options.jobIds) && { job: { id: { in: options.jobIds } } },
      arrayHasValues(options.countryIds) && { location: { countryId: { in: options.countryIds } } },
      arrayHasValues(options.locationIds) && { location: { id: { in: options.locationIds } } },
      arrayHasValues(allowedJobFamilyIds) && { job: { familyId: { in: allowedJobFamilyIds } } },
      arrayHasValues(allowedCountryIds) && { location: { countryId: { in: allowedCountryIds } } },
      !ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS &&
        arrayHasValues(options.levels) && { level: { in: options.levels } },
      !ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS &&
        arrayHasValues(allowedLevels) && { level: { in: allowedLevels } },
      ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS &&
        options.benchmarkLevelsRange &&
        options.benchmarkLevelsRange?.max > 0 && {
          AND: [
            {
              OR: [
                {
                  benchmarkLevelMin: { lte: options.benchmarkLevelsRange.max },
                  benchmarkLevelMax: { gte: options.benchmarkLevelsRange.min },
                },
                {
                  benchmarkLevelMin: { gte: options.benchmarkLevelsRange.min },
                  benchmarkLevelMax: { lte: options.benchmarkLevelsRange.max },
                },
              ],
            },
            {
              NOT: {
                AND: [{ benchmarkLevelMin: null }, { benchmarkLevelMax: null }],
              },
            },
          ],
        },
    ]),
  } satisfies Prisma.DatasetEmployeeWhereInput;
};

const RefBaseSalaries: { [key in EmployeeLevel]: number } = {
  BEGINNER: 35_000,
  JUNIOR: 40_000,
  INTERMEDIATE: 45_000,
  SENIOR: 50_000,
  STAFF: 65_000,
  PRINCIPAL: 75_000,
  TEAM_LEAD: 60_000,
  MANAGER: 80_000,
  HEAD_OF: 100_000,
  DIRECTOR: 120_000,
  VP: 130_000,
  C_LEVEL: 140_000,
};

const getRandomNumberInRange = (params: { min: number; max: number }) => {
  const min = Math.ceil(params.min);
  const max = Math.floor(params.max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const anonymiseMarketDataDatasetEmployee = (employee: MarketDataDatasetEmployee): MarketDataDatasetEmployee => {
  // Base salaries are based on level and added a +/- 20% random variation
  const refBaseSalary = RefBaseSalaries[employee.level];
  const variation = getRandomNumberInRange({ min: -20, max: 20 }) / 100;
  const baseSalary = Math.round(refBaseSalary * (1 + variation) * 100);

  // 25% of employees have fixed bonuses between 100€ & 10000€
  const hasFixedBonus = getRandomNumberInRange({ min: 0, max: 100 }) <= 25;
  const fixedBonus = hasFixedBonus ? roundTo(getRandomNumberInRange({ min: 100, max: 10000 }), 100) * 100 : null;

  // 100% of sales have variable bonuses of 50% of their base salary with 80% realistic payout rate
  // 50% of marketing have 50% variable bonuses of 50% of their base salary with 80% realistic payout rate
  // Other job families don't have variable bonuses
  const variableBonusProbability = employee.jobId === 11 ? 100 : employee.jobId === 6 ? 50 : 0;
  const hasVariableBonus = getRandomNumberInRange({ min: 0, max: 100 }) <= variableBonusProbability;
  const onTargetBonus = hasVariableBonus ? baseSalary * 0.5 : null;

  // 60% of employees are male
  const gender = getRandomNumberInRange({ min: 0, max: 100 }) <= 60 ? Gender.MALE : Gender.FEMALE;

  return {
    ...employee,

    externalId: `${employee.id}`,

    baseSalary,
    onTargetBonus,
    fixedBonus,

    gender,
  };
};
